import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, Button, Form} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {Collapse} from 'react-collapse';
import {StyledActivateCardModal} from './StyledActivateCardModal';
import DigitInput from '../../../DigitInput';
import NumericPasswordInput from '../../../NumericPasswordInput';
import {formHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';

const {Item} = Form;

const defaultFormValues = {
  four_last_digits: '',
  confirm_PIN: '',
  new_PIN: '',
  public_token: '',
};

const fieldsLength = {
  pin: 4,
  token: 9
}

const ActivateCardModal = ({
  loading,
  open,
  onOk,
  onCancel,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'cards']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  const inputProps = {
    placeholder: t('4 digits'),
    size: 'large',
  }

  const passwordInputProps = {
    ...inputProps,
    maxLength: 4,
  }

  const handleAfterClose = () => {
    form.setFieldsValue(defaultFormValues);
    error && setError(null);
  }

  const handleSubmit = (formData) => {
    if (!onOk) return;

    onOk({
      data: formData,
      errorCallback: (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
          setError(responseData.detail);
        } else {
          const fields = formHelpers.getFormServerErrorFields(responseData);
          form.setFields(fields);
        }
      },
    });
  };

  const validationRules = useMemo(() => ({
    password: validation.password(t, fieldsLength.pin),
    passwordConfirm: validation.passwordConfirm(t, fieldsLength.pin, 'new_PIN'),
    token: validation.password(t, fieldsLength.token),
  }), [t]);

  const footer = [
    <Button
      key='cancel'
      onClick={onCancel}
      size='large'
    >
      {t('cancel')}
    </Button>,
    <Button
      key='submit'
      loading={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {t('submit')}
    </Button>
  ];

  const tooltip = (key) => ({ icon: <InfoCircleOutlined />, title: t(`cards:modal.activateCard.tooltips.${key}`) });

  const warningMessage = (
    <>{t('cards:modal.activateCard.warning.description1')} <strong>{t('cards:modal.activateCard.warning.description2')}</strong></>
  );

  return (
    <StyledActivateCardModal
      afterClose={handleAfterClose}
      forceRender
      footer={footer}
      onCancel={onCancel}
      open={open}
      title={`${t('card')} ${t('activation')}`}
      width={430}
      {...rest}
    >
      <Form
        disabled={loading}
        initialValues={defaultFormValues}
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
      >
        <Item
          label={t('token')}
          name='public_token'
          rules={validationRules.token}
          required
          tooltip={tooltip('token')}
        >
          <DigitInput
            {...inputProps}
            maxLength={fieldsLength.token}
            placeholder={t('token')}
          />
        </Item>
        <Item
          label={t('last4Digits')}
          name='four_last_digits'
          rules={validationRules.password}
          required
          tooltip={tooltip('last4Digits')}
        >
          <DigitInput
            {...inputProps}
            maxLength={fieldsLength.pin}
            placeholder={t('last4Digits')}
          />
        </Item>
        <Item
          label='PIN'
          name='new_PIN'
          rules={validationRules.password}
          required
        >
          <NumericPasswordInput {...passwordInputProps} />
        </Item>
        <Item
          label={`${t('confirm')} PIN`}
          name='confirm_PIN'
          rules={validationRules.passwordConfirm}
          required
        >
          <NumericPasswordInput {...passwordInputProps} />
        </Item>

        <Collapse isOpened={error}>
          <Alert message={error} type='error' showIcon />
        </Collapse>

        <Alert message={warningMessage} type='warning' showIcon />
      </Form>

    </StyledActivateCardModal>
  );
}

ActivateCardModal.propTypes = {
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}

export default ActivateCardModal;
